import {
  Avatar as MantineAvatar,
  CheckIconProps,
  Combobox,
  ComboboxProps,
  Divider,
  Flex,
  Group,
  Input,
  InputBase,
  ScrollArea,
  Text,
  useCombobox,
} from "@mantine/core";
import { IUserPartial } from "@schemas/issueSchema";
import { IconCaretDownFilled, IconCheck, IconX } from "@tabler/icons-react";
import { useRef, useState } from "react";
import Avatar from "../Avatar";

function findItemByValue(value: string, data: IUserPartial[]): IUserPartial | null {
  return data.find((entry) => entry._id === value) ?? null;
}

const iconProps: CheckIconProps = {
  color: "currentColor",
  opacity: 0.6,
  size: 18,
};

type McMantineMultiUserSelectProps = Omit<ComboboxProps, "children" | "defaultValue" | "data" | "onChange"> & {
  data: IUserPartial[];
  /** Called ONLY when dropdown closes with final selection (not on Escape) */
  onChange?: (value: IUserPartial[]) => void;
  defaultValue?: IUserPartial[];
  placeholder?: string;
  displayVariant?: "default" | "tableCell";
};

export function McMantineMultiUserSelect({
  data,
  onChange,
  defaultValue = [],
  placeholder,
  displayVariant = "default",
  ...props
}: McMantineMultiUserSelectProps) {
  const [values, setValues] = useState<IUserPartial[]>(defaultValue);
  const [search, setSearch] = useState<string>("");
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const MAX_VISIBLE_AVATARS = 10;
  const hiddenCount = Math.max(0, values.length - MAX_VISIBLE_AVATARS);
  const visibleUsers = values.slice(0, MAX_VISIBLE_AVATARS);
  const usersToRender = hiddenCount > 0 ? values.slice(0, MAX_VISIBLE_AVATARS - 1) : visibleUsers;

  const combobox = useCombobox({
    onDropdownOpen: () => {
      setHasOpenedOnce(true);
      setSearch("");

      combobox.updateSelectedOptionIndex("active");
      setTimeout(() => searchRef.current?.focus(), 0);
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      onChange?.(values);
    },
  });

  const toggleValue = (val: IUserPartial) =>
    setValues((current) =>
      current.some((v) => v._id === val._id) ? current.filter((v) => v._id !== val._id) : [...current, val],
    );

  const removeValue = (id: string) => setValues((current) => current.filter((v) => v._id !== id));

  const matchesSearch = (item: IUserPartial) => item.name.toLowerCase().includes(search.trim().toLowerCase());

  const renderOptionFromItem = (item: IUserPartial, isSelected: boolean) => {
    return (
      <Combobox.Option value={item._id} key={item._id} active={isSelected}>
        <Group flex="1" gap="xs" wrap="nowrap">
          <Avatar id={item._id} name={item.name} size="sm" />
          <Flex style={{ minWidth: 0, flex: 1 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
          </Flex>
          {isSelected ? (
            <IconX size={16} style={{ marginInlineStart: "auto", opacity: 0.6 }} />
          ) : (
            <IconCheck style={{ marginInlineStart: "auto", visibility: "hidden" }} {...iconProps} />
          )}
        </Group>
      </Combobox.Option>
    );
  };
  const avatarGroup = (
    <MantineAvatar.Group
      spacing="xs"
      style={{
        flexWrap: "nowrap",
        overflow: "hidden",
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      {usersToRender.map((item) => (
        <Avatar
          key={item._id}
          id={item._id}
          name={item.name}
          size="sm"
          style={{ cursor: "pointer" }}
        />
      ))}

      {hiddenCount > 0 && (
        <MantineAvatar size="sm" style={{ cursor: "pointer" }}>
          +{hiddenCount}
        </MantineAvatar>
      )}
    </MantineAvatar.Group>
  );

  // Separate selected and unselected users
  const selectedOptions = values
    .filter((v) => matchesSearch(v))
    .map((entry) => renderOptionFromItem(entry, true));

  const unselectedOptions = data
    .filter((entry) => !values.some((v) => v._id === entry._id) && matchesSearch(entry))
    .map((entry) => renderOptionFromItem(entry, false));

  const hasSelectedOptions = selectedOptions.length > 0;
  const hasUnselectedOptions = unselectedOptions.length > 0;
  const isTableCellVariant = displayVariant === "tableCell";

  return (
    <Combobox
      store={combobox}
      resetSelectionOnOptionHover
      withinPortal
      onOptionSubmit={(val) => {
        const isAlreadySelected = values.some((entry) => entry._id === val);
        if (isAlreadySelected) {
          removeValue(val);
          return;
        }

        const entry = findItemByValue(val, data);
        if (!entry) return;
        toggleValue(entry);
      }}
      {...props}
    >
      <Combobox.Target>
        <InputBase
          styles={{
            root: {
              backgroundColor: isTableCellVariant ? "transparent" : undefined,
              border: isTableCellVariant ? "none" : undefined,
            },
            input: {
              outline: "none",
              boxShadow: "none",
              backgroundColor: isTableCellVariant ? "transparent" : undefined,
              border: isTableCellVariant ? "none" : undefined,
              paddingInlineStart: isTableCellVariant ? 0 : undefined,
              paddingInlineEnd: isTableCellVariant ? 0 : undefined,
            },
          }}
          component="button"
          type="button"
          pointer
          rightSection={<IconCaretDownFilled size={17} color="var(--mc-icon-default)" />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {values.length > 0 ? (
            <Flex miw={0} style={{ overflow: "hidden" }}>
              {avatarGroup}
            </Flex>
          ) : (
            <>{isTableCellVariant ? null : <Input.Placeholder>{placeholder ?? "Velg bruker"}</Input.Placeholder>}</>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown miw={220}>
        {data.length > 10 && (
          <Combobox.Search
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Søk..."
          />
        )}
        <Combobox.Options>
          {hasOpenedOnce && (
            <ScrollArea.Autosize mah={280} type="scroll">
              {!hasSelectedOptions && !hasUnselectedOptions && (
                <Combobox.Empty>Ingen brukere funnet</Combobox.Empty>
              )}

              {hasSelectedOptions && (
                <>
                  <Text size="xs" c="dimmed" px="sm" pt="xs" pb={4}>
                    Valgt ({values.length})
                  </Text>
                  {selectedOptions}
                </>
              )}

              {hasSelectedOptions && hasUnselectedOptions && (
                <Divider my="xs" />
              )}

              {hasUnselectedOptions && unselectedOptions}
            </ScrollArea.Autosize>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
